"use client";

import React from "react";
import { BiPlus, BiSearchAlt } from "react-icons/bi";

interface PageHeaderProps {
  title: string;
  subtitle: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showButton?: boolean;
  buttonLabel?: string;
  onButtonClick?: () => void;
  buttonIcon?: React.ReactNode;
  buttonClassName?: string;
  buttonDisabled?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  showSearch = false,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  showButton = false,
  buttonLabel = "Add",
  onButtonClick,
  buttonIcon,
  buttonClassName,
  buttonDisabled = false,
}) => {
  const isSearchControlled = typeof onSearchChange === "function";
  const renderedButtonIcon = buttonIcon ?? (
    <BiPlus size={18} className="text-white" />
  );
  const desktopButtonClassName =
    buttonClassName ??
    "bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-60 disabled:cursor-not-allowed";
  const mobileButtonClassName =
    buttonClassName ??
    "bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div className="mb-8">
      <div className="hidden md:flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0f1f4b]">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>

        <div className="flex items-center gap-3">
          {showSearch && (
            <div className="relative">
              <BiSearchAlt
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={isSearchControlled ? (searchValue ?? "") : undefined}
                onChange={
                  isSearchControlled
                    ? (event) => onSearchChange(event.target.value)
                    : undefined
                }
                className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {showButton && (
            <button
              onClick={onButtonClick}
              disabled={buttonDisabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${buttonDisabled ? "cursor-not-allowed" : "cursor-pointer"} ${desktopButtonClassName}`}
            >
              {renderedButtonIcon}
              {buttonLabel}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 md:hidden mt-8">
        <div>
          <h1 className="text-lg font-bold text-[#0f1f4b]">{title}</h1>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>

        {(showSearch || showButton) && (
          <div className="flex items-center gap-2">
            {showSearch && (
              <div className="relative flex-1">
                <BiSearchAlt
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={isSearchControlled ? (searchValue ?? "") : undefined}
                  onChange={
                    isSearchControlled
                      ? (event) => onSearchChange(event.target.value)
                      : undefined
                  }
                  className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {showButton && (
              <button
                onClick={onButtonClick}
                disabled={buttonDisabled}
                className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-medium transition-colors shrink-0 ${buttonDisabled ? "cursor-not-allowed" : "cursor-pointer"} ${mobileButtonClassName}`}
              >
                {renderedButtonIcon}
                {buttonLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
